/* ------------------------------------------------------------------ */
/*  Extraction control UI logic (PRD.273) — pure + testable.           */
/*                                                                      */
/*  Drives the recording-card / transcript-drawer extraction action     */
/*  label + enabled state from primitive inputs, so the card and drawer */
/*  share identical behavior and it can be unit-tested without React.   */
/* ------------------------------------------------------------------ */

export interface ExtractionUiState {
  /** A non-empty assembled transcript exists to extract from. */
  hasTranscript: boolean;
  /** Extraction is currently running. */
  running: boolean;
  /** The app is offline. */
  offline: boolean;
  /** A completed extraction already exists for the current transcript. */
  completed: boolean;
  /** The last extraction attempt failed. */
  failed: boolean;
  /** The user has Clinical Extraction run permission. */
  canRun: boolean;
  /** An automatic first-run extraction is expected to start shortly (PRD.273). */
  autoPending?: boolean;
}

/** Button label per PRD.273 state list. */
export function extractionActionLabel(s: ExtractionUiState): string {
  if (s.running) return 'Extracting…';
  if (s.offline) return 'Extract when online';
  if (s.failed) return 'Retry extraction';
  if (s.completed) return 'Refresh extraction';
  // No completed extraction: the normal flow auto-starts after transcription.
  if (s.autoPending) return 'Waiting to extract';
  return 'Extract clinical details';
}

/** Extraction is disabled without a transcript, offline, while running, or without permission. */
export function isExtractionActionDisabled(s: ExtractionUiState): boolean {
  return !s.hasTranscript || s.offline || s.running || !s.canRun;
}

/** Accessible helper/tooltip text explaining why the action is disabled (or null). */
export function extractionDisabledReason(s: ExtractionUiState): string | null {
  if (!s.canRun) return null; // action hidden for non-runners; no tooltip needed
  if (s.running) return null;
  if (!s.hasTranscript) return 'Transcript is not available yet.';
  if (s.offline) return 'Waiting for network';
  return null;
}

/**
 * Compact, PHI-free error text for the card. Never surfaces raw provider text —
 * detailed diagnostics stay in the existing safe observability path.
 */
export function compactExtractionError(failed: boolean): string | null {
  return failed ? 'Extraction failed.' : null;
}

/* ── Automatic extraction after transcription (PRD.273 change request) ── */

export interface AutoExtractDecisionInput {
  /** Feature gate — the extraction endpoint is configured. */
  enabled: boolean;
  /** Clinical Extraction run permission. */
  canRun: boolean;
  /** App is offline (no auto-run; re-evaluated on reconnect). */
  offline: boolean;
  /** Extraction is already running. */
  running: boolean;
  /** A non-empty assembled transcript is available + persisted. */
  hasTranscript: boolean;
  /** Stable key for the current assembled transcript (trimmed transcript). */
  transcriptKey: string;
  /** The transcript of the current COMPLETED extraction (trimmed), or null. */
  lastCompletedKey: string | null;
  /** The transcript key we last auto-triggered (session ref), or null. */
  lastAttemptedKey: string | null;
}

/**
 * Decide whether to automatically start extraction after transcription.
 * Exactly one provider request per new assembled-transcript key: guarded by the
 * current completed extraction AND a session "last attempted" key so React
 * re-renders, PowerSync refreshes, and rehydration never double-fire. Offline
 * blocks the run; a reconnect re-evaluates and fires once if still un-extracted.
 */
export function shouldAutoExtract(i: AutoExtractDecisionInput): boolean {
  if (!i.enabled || !i.canRun) return false;
  if (i.offline || i.running) return false;
  if (!i.hasTranscript || !i.transcriptKey) return false;
  // Already extracted this exact transcript → server would return cached; skip.
  if (i.lastCompletedKey !== null && i.lastCompletedKey === i.transcriptKey) return false;
  // Already auto-triggered this transcript this session → don't duplicate.
  if (i.lastAttemptedKey === i.transcriptKey) return false;
  return true;
}
